import React, { useEffect, useRef, useCallback } from 'react';
import { FaPaperPlane, FaSpinner } from 'react-icons/fa';
import { 
    Wrapper,
    UserTextInput, 
    SendMessageButton, 
    ActionsContainer, 
    BotIntroduction,
    HistoryMessages,
    UserMessageContainer,
    BotMessageContainer,
    BotLoadingMessage
} from './style.css.js';

const Message = ({ message, $dark }) => {
    if( message.type === 'user' ){
        return (
            <UserMessageContainer $dark={$dark}>
                <p>{ message.message }</p>
                <span>{ message.timestamp }</span>
            </UserMessageContainer>
        )
    }
    if( message.type === 'bot' ){
        return (
            <BotMessageContainer $dark={$dark}>
                <p>{ message.message }</p>
                <span>{ message.timestamp }</span>
            </BotMessageContainer>
        )
    }
    return null;
}

const ChatAnimation = ({ users, messages, darkTheme = false }) => {
    const isDark = !!darkTheme;

    const [userInput, setUserInput] = React.useState('');
    const [isBotLoading, setIsBotLoading] = React.useState(false);
    const [historyMessages, setHistoryMessages] = React.useState([]);
    
    const sendMessageButtonRef = React.useRef(null);
    const historyMessagesRef = useRef(null);

    const clickSendButton = useCallback(() => {
        if(!sendMessageButtonRef.current) 
            return;
        sendMessageButtonRef.current.style.transform = 'scale(1.1)';
        setTimeout(() => {
            if (sendMessageButtonRef.current) {
                sendMessageButtonRef.current.style.transform = 'scale(1)';
            }
        }, 100);
    }, []);

    const startTypingMessageSimulation = useCallback((message) => {
        let currentIndex = 0;
        const typingInterval = setInterval(() => {
            setUserInput(message.message.slice(0, currentIndex + 1));
            currentIndex++;   
            // End of typing, simulate user clicking send button
            if (currentIndex === message.message.length) {
                clearInterval(typingInterval);
                setTimeout(() => {
                    clickSendButton();
                    setHistoryMessages(prevMessages => [...prevMessages, {
                        ...message
                    }]);
                    setUserInput('');
                }, 300);
            }
        }, 10);
        return () => clearInterval(typingInterval);
    }, [clickSendButton]);

    useEffect(() => {
        if( !historyMessages.length ){
            const cleanup = startTypingMessageSimulation(messages[0]);
            return () => cleanup();
        }

        const lastMessage = historyMessages[historyMessages.length - 1];
        const lastMessageIndex = messages.findIndex(msg => msg.id === lastMessage.id);
        if( lastMessageIndex === -1 ){
            return;
        }

        const nextMessage = messages[lastMessageIndex + 1];
        if( !nextMessage ){
            return;
        }

        if(nextMessage.type === 'user'){
            let cleanupTyping = null;
            const timeout = setTimeout(() => {
                cleanupTyping = startTypingMessageSimulation(nextMessage);
            }, 1500);
            return () => {
                clearTimeout(timeout);
                if (cleanupTyping) cleanupTyping();
            };
        }
        if(nextMessage.type === 'bot'){
            setIsBotLoading(true);
            // Wait 1.5 seconds and then set isBotLoading to false
            const timeout = setTimeout(() => {
                setIsBotLoading(false);
                setHistoryMessages(prevMessages => [...prevMessages, {
                    ...nextMessage
                }]);
            }, 2500);
            return () => clearTimeout(timeout);
        }
    }, [historyMessages, messages, startTypingMessageSimulation]);

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        if (historyMessagesRef.current) {
            historyMessagesRef.current.scrollTo({
                top: historyMessagesRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, [historyMessages, isBotLoading]);
    
    return (
        <Wrapper $dark={isDark}>
            <HistoryMessages ref={historyMessagesRef}>
                <BotIntroduction $dark={isDark}>
                    <img 
                        src="/assets/logo-xl-mini.png" 
                        alt="Synaptek Logo" 
                    />
                    <p>
                        Bienvenue chez Synaptek, votre e-commerce en intelligence artificielle.
                        En quoi puis-je vous aider aujourd'hui ?
                    </p>
                </BotIntroduction>

                { historyMessages.map(message => (
                    <Message key={message.id} message={message} $dark={isDark} />
                )) }

                { isBotLoading && (
                    <BotLoadingMessage $dark={isDark}>
                        <FaSpinner id="spinner" size={20} />
                        <span>Réflexion en cours...</span>
                    </BotLoadingMessage>
                )}
            </HistoryMessages>

            <ActionsContainer>
                <UserTextInput 
                    type="text" 
                    placeholder="Demandez à l'assistant IA..."
                    value={userInput}
                    onChange={() => {}}
                    disabled
                    $dark={isDark}
                />
                <SendMessageButton ref={sendMessageButtonRef}>
                    <FaPaperPlane
                        style={{
                            fontSize: '16px',
                            color: '#FFFFFF',
                        }}
                    />
                </SendMessageButton>
            </ActionsContainer>
        </Wrapper>
    );
}

export default ChatAnimation;